import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  AiListeningCampaignDeleteResponse,
  AiListeningCampaignListResponse,
  AiListeningCampaignPayload,
  AiListeningCampaignSingleResponse,
  AiListeningOverviewResponse,
  AiListeningResponseListResponse,
  BrandsAlongsideResponse,
  PromptResultsResponse,
  ReloadHistoryResponse,
  ReloadResponse,
  ReloadStatusResponse,
  SmartInsightsResponse,
} from 'src/services/__generated__/api'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class AiListeningApiService {
  private static _instance: AiListeningApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): AiListeningApiService {
    if (!AiListeningApiService._instance) {
      AiListeningApiService._instance = new AiListeningApiService()
    }
    return AiListeningApiService._instance
  }

  public static refresh(): void {
    AiListeningApiService._instance = new AiListeningApiService()
  }

  public static async getOverview(): Promise<AiListeningOverviewResponse> {
    const response =
      await AiListeningApiService.instance.client.get<AiListeningOverviewResponse>(
        '/v1_private/ai-listening/overview',
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getList(): Promise<AiListeningCampaignListResponse> {
    const response =
      await AiListeningApiService.instance.client.get<AiListeningCampaignListResponse>(
        '/v1_private/ai-listening/campaign',
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getById(
    id: string
  ): Promise<AiListeningCampaignSingleResponse> {
    const response =
      await AiListeningApiService.instance.client.get<AiListeningCampaignSingleResponse>(
        `/v1_private/ai-listening/campaign/${id}`,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async create(
    payload: AiListeningCampaignPayload
  ): Promise<AiListeningCampaignSingleResponse> {
    const response =
      await AiListeningApiService.instance.client.post<AiListeningCampaignSingleResponse>(
        '/v1_private/ai-listening/campaign',
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async update(
    id: string,
    payload: AiListeningCampaignPayload
  ): Promise<AiListeningCampaignSingleResponse> {
    const response =
      await AiListeningApiService.instance.client.put<AiListeningCampaignSingleResponse>(
        `/v1_private/ai-listening/campaign/${id}`,
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getPromptResults(
    campaignId: string,
    checkedAt?: string
  ): Promise<PromptResultsResponse> {
    const params = checkedAt ? { checkedAt } : undefined
    const response =
      await AiListeningApiService.instance.client.get<PromptResultsResponse>(
        `/v1_private/ai-listening/campaign/${campaignId}/prompt-results`,
        { headers: getHeaders(), params }
      )
    return response.data
  }

  public static async getBrandsAlongside(
    campaignId: string,
    checkedAt?: string
  ): Promise<BrandsAlongsideResponse> {
    const params = checkedAt ? { checkedAt } : undefined
    const response =
      await AiListeningApiService.instance.client.get<BrandsAlongsideResponse>(
        `/v1_private/ai-listening/campaign/${campaignId}/brands-alongside`,
        { headers: getHeaders(), params }
      )
    return response.data
  }

  public static async getResponses(
    campaignId: string,
    checkedAt?: string
  ): Promise<AiListeningResponseListResponse> {
    const params = checkedAt ? { checkedAt } : undefined
    const response =
      await AiListeningApiService.instance.client.get<AiListeningResponseListResponse>(
        `/v1_private/ai-listening/campaign/${campaignId}/responses`,
        { headers: getHeaders(), params }
      )
    return response.data
  }

  public static async getReloadStatus(
    campaignId: string
  ): Promise<ReloadStatusResponse> {
    const response =
      await AiListeningApiService.instance.client.get<ReloadStatusResponse>(
        `/v1_private/ai-listening/campaign/${campaignId}/reload-status`,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async reload(id: string): Promise<ReloadResponse> {
    const response =
      await AiListeningApiService.instance.client.post<ReloadResponse>(
        `/v1_private/ai-listening/campaign/${id}/reload`,
        {},
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getReloadHistory(
    campaignId: string
  ): Promise<ReloadHistoryResponse> {
    const response =
      await AiListeningApiService.instance.client.get<ReloadHistoryResponse>(
        `/v1_private/ai-listening/campaign/${campaignId}/reload-history`,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getSmartInsights(
    campaignId: string,
    checkedAt?: string
  ): Promise<SmartInsightsResponse> {
    const params = checkedAt ? { checkedAt } : undefined
    const response =
      await AiListeningApiService.instance.client.get<SmartInsightsResponse>(
        `/v1_private/ai-listening/campaign/${campaignId}/smart-insights`,
        { headers: getHeaders(), params }
      )
    return response.data
  }

  public static async duplicate(
    id: string
  ): Promise<AiListeningCampaignSingleResponse> {
    const response =
      await AiListeningApiService.instance.client.post<AiListeningCampaignSingleResponse>(
        `/v1_private/ai-listening/campaign/${id}/duplicate`,
        {},
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async delete(
    id: string
  ): Promise<AiListeningCampaignDeleteResponse> {
    const response =
      await AiListeningApiService.instance.client.delete<AiListeningCampaignDeleteResponse>(
        `/v1_private/ai-listening/campaign/${id}`,
        { headers: getHeaders() }
      )
    return response.data
  }
}

export { AiListeningApiService }
